import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Loader2, Lock, MessageCircleQuestion, ChevronRight, Brain, ShieldAlert } from "lucide-react";

export default function InterviewPrep() {
  const { user } = useAuth();
  const loc = useLocation();
  const preJob = loc.state?.job;
  const [resume, setResume] = useState("");
  const [jobDesc, setJobDesc] = useState(preJob?.description ? preJob.description.replace(/<[^>]+>/g, " ") : "");
  const [company, setCompany] = useState(preJob?.company || "");
  const [jobTitle, setJobTitle] = useState(preJob?.title || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const isPro = user?.plan === "pro";

  useEffect(() => {
    if (!user) return;
    api.get("/resume").then(({ data }) => { if (data.text) setResume(data.text); }).catch(() => {});
  }, [user]);

  const run = async () => {
    if (!isPro) { toast.error("Upgrade to Pro for interview prep"); return; }
    if (!resume.trim() || !jobDesc.trim()) { toast.error("Add resume and job description"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/ai/interview-prep", { resume, job_description: jobDesc, company, job_title: jobTitle });
      setResult(data);
      toast.success("Prep kit ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow">AI Interview Prep</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Prep like you've already got the offer</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">Role-specific questions, STAR stories mapped to your resume, tech topics to refresh, and sharp questions to ask.</p>
        </div>
        {!isPro && (
          <Link to="/pricing" className="nb-btn bg-[#ec4899] text-white px-5 py-2.5 inline-flex items-center gap-2">
            <Lock className="w-4 h-4" /> Unlock with Pro
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <h2 className="text-xl font-bold">Inputs</h2>
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="prep-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company"
                className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
              <input data-testid="prep-title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Job title"
                className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Your resume</label>
              <textarea data-testid="prep-resume" value={resume} onChange={(e) => setResume(e.target.value)} rows={8}
                className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca] font-mono text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Job description</label>
              <textarea data-testid="prep-jd" value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} rows={8}
                className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca] font-mono text-sm" />
            </div>
            <button data-testid="prep-run" onClick={run} disabled={busy}
              className="nb-btn bg-[#4338ca] text-white px-5 py-3 w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Prepping…</> : <><Sparkles className="w-4 h-4" /> Build prep kit</>}
            </button>
          </div>
        </div>

        <div className="nb-card p-6" data-testid="prep-result">
          <h2 className="text-xl font-bold">Prep kit</h2>
          {!result ? (
            <div className="mt-10 text-center text-gray-500">
              <MessageCircleQuestion className="w-10 h-10 mx-auto text-[#4338ca]" />
              <p className="mt-3 font-bold">Your interview prep will appear here.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-5">
              {result.likely_questions?.length > 0 && (
                <div>
                  <div className="label-eyebrow">Likely questions</div>
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {result.likely_questions.map((q, i) => (
                      <li key={i} className="flex gap-2"><ChevronRight className="w-4 h-4 mt-0.5 text-[#ec4899]" strokeWidth={3} />{q}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.behavioral_stories?.length > 0 && (
                <div>
                  <div className="label-eyebrow">STAR stories from your resume</div>
                  <div className="mt-2 space-y-3">
                    {result.behavioral_stories.map((s, i) => (
                      <div key={i} className="border-2 border-black rounded-lg p-3 bg-[#FEFCE8]">
                        <div className="text-xs font-black uppercase tracking-widest">{s.question_bucket}</div>
                        <div className="mt-1 text-sm"><strong>S:</strong> {s.situation}</div>
                        <div className="text-sm"><strong>T:</strong> {s.task}</div>
                        <div className="text-sm"><strong>A:</strong> {s.action}</div>
                        <div className="text-sm"><strong>R:</strong> {s.result}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {result.technical_topics?.length > 0 && (
                <div>
                  <div className="label-eyebrow">Refresh these topics</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {result.technical_topics.map((t, i) => (
                      <span key={i} className="nb-pill bg-[#4338ca] text-white text-[11px] inline-flex items-center gap-1"><Brain className="w-3 h-3" />{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {result.questions_to_ask_them?.length > 0 && (
                <div>
                  <div className="label-eyebrow">Ask them</div>
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {result.questions_to_ask_them.map((q, i) => (
                      <li key={i} className="flex gap-2"><MessageCircleQuestion className="w-4 h-4 mt-0.5 text-[#22c55e]" strokeWidth={2.5} />{q}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.red_flags_to_watch?.length > 0 && (
                <div>
                  <div className="label-eyebrow">Red flags to probe</div>
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {result.red_flags_to_watch.map((r, i) => (
                      <li key={i} className="flex gap-2"><ShieldAlert className="w-4 h-4 mt-0.5 text-[#ec4899]" strokeWidth={2.5} />{r}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
